import { HostListener, Injectable, OnDestroy } from '@angular/core';
import { GraphicsRenderDecal } from '../digital-twin/graphics/graphics-render-decal';
import { CollectionService } from '../collections/collection.service';
import * as THREE from '@phdsoft/three';
import { Asset } from '../platform/busines-data/Framework/Asset';
import { InspectionCampaign } from '../platform/busines-data/Campaigns/InspectionCampaign';
import { StructureCollection } from '../platform/busines-data/Campaigns/StructureCollection';
import { GaugingElement } from '../platform/busines-data/SectionElements/InspectionElements/gaugingElement';
import { WorkableElement } from '../platform/busines-data/SectionElements/WorkableElement';
import { GraphicsMaterials } from '../digital-twin/graphics/graphics-materials';
import { GaugingPoint } from '../platform/busines-data/SectionElements/InspectionElements/gaugingPoint';
import { SteelThicknessGaugingPoint } from '../platform/busines-data/SectionElements/InspectionElements/steelThicknessGaugingPoint';
import { StructuralElement } from '../platform/busines-data/SectionElements/StructureElements/StructuralElement';
import { ElementResultInfo } from 'projects/phdsoft/common/src/lib/models/element-result-info.model';
import { RepairCampaign } from '../platform/busines-data/Campaigns/RepairCampaign';
import { ELEMENTSTYPESIDS } from 'projects/phdsoft/common/src/lib/shared/constants/elementsTypesIds';
import { INSPECTIONS_TYPES_IDS } from 'projects/phdsoft/common/src/lib/shared/constants/inspections-types-ids';
import { INSERTIONCONSTANTS } from 'projects/phdsoft/common/src/lib/shared/constants/insertions-constants';
import { Collection } from '../platform/busines-data/Campaigns/Collection';
import { BasicElement } from '../platform/busines-data/SectionElements/BasicElement/BasicElement';
import { InspectionEventsService } from '../template/view-mode-tools/view-mode-anomaly-labels/inspection-events.service';
import { isStiffenerTypeId, orphanNormal, orphanPointShapeFor } from './orphan-point-shape.util';
import { InspectionPointInstanceManager } from '../digital-twin/graphics/inspection-point-instance-manager.service';
import { PointShapeType } from '../digital-twin/graphics/point-shape-geometry-cache';
import { PhantomSurfaceDesign } from '../digital-twin/graphics/phantom-surface-design';
import { Section } from '../platform/busines-data/Framework/Section';

/** One (section, shape) group of POINT inspections collected in pass 1 of loadInspectionElements. */
export interface PendingBatch {
  section: Section;
  shapeType: PointShapeType;
  items: { element: GaugingElement; matrix: THREE.Matrix4; color: THREE.Color }[];
}

/** Pipe diameter (mm) given to orphan pipe points: returnPipePoint scales by diameter * 0.003, so this matches the plate point scale (2). */
const ORPHAN_PIPE_DIAMETER = 2 / 0.003;
/** Edge (m) of the box passed to plate/beam-part/cable builders when the section is not loaded (the builders' scale does not depend on it). */
const ORPHAN_DEFAULT_BOX_SIZE = 1;

@Injectable({ providedIn: 'root' })
export class AssetManagerDownloadedInspection implements OnDestroy {

  private referenceDate: Date;

  /**
   * Reusable Object3D for matrix calculation (dummy pattern for setMatrixAt).
   * Allocated once to avoid GC pressure during bulk loads.
   */
  private readonly _dummy = new THREE.Object3D();

  constructor(
    private graphicsRenderDecal: GraphicsRenderDecal,
    private collectionService: CollectionService,
    private inspectionEventsService: InspectionEventsService,
    private instanceManager: InspectionPointInstanceManager,
  ) {
  }

  @HostListener('unloaded')
  ngOnDestroy(): void {
    console.log('💣 Destroy AssetManagerDownloadedInspection');
  }

  /** Number of elements to process per chunk before yielding to the main thread. */
  private static readonly CHUNK_SIZE = 25;

  /** Yield control to the main thread so the render loop and UI events can run. */
  private _yieldToMainThread(): Promise<void> {
    return new Promise(resolve => setTimeout(resolve, 0));
  }

  public async loadInspectionElements(asset: Asset, elements: ElementResultInfo[], regionId: string, disposePoint: boolean, forceLoad: boolean, referenceDate: Date, changeInspectionVisibility: boolean = true): Promise<void> {
    referenceDate.setHours(0, 0, 0, 0);
    this.referenceDate = referenceDate;
    let collection = asset.InspectionCampaigns[0];
    if (!collection.loaded || forceLoad) {
      const campaigns = asset.StructureCampaigns as Array<Collection>;
      campaigns.push(asset.ProtectionElementsCollection);
      const initialElementCount = collection.Elements.length;

      // Set the static service-locator on GaugingElement so the active setter
      // and realVisibility override can reach the manager (VIS-04 / VIS-06).
      GaugingElement.instanceManager = this.instanceManager;

      // Track which sections had POINT inspection elements added so we can
      // attach InstancedMesh objects to their surfaceGroup after the loop.
      const processedSections = new Set<Section>();

      // Accumulate POINT elements by (section, shapeType) group. New groups become
      // exact-capacity batches (buildBatch); groups whose batch already exists
      // (second plan/filter, inserted point, SignalR) are appended with addInstance.
      const pendingByBatch = new Map<string, PendingBatch>();

      // O(1) lookup map — replaces O(n) findIndex per element (eliminates O(n²) bottleneck)
      const elementLookup = new Map<string, number>();
      for (let i = 0; i < collection.Elements.length; i++) {
        const el = collection.Elements[i];
        elementLookup.set(`${el.Guid}:${el.regionId}`, i);
      }

      for (let i = 0; i < elements.length; i++) {
        const _elementResult = elements[i];
        const existingIndex = elementLookup.get(`${_elementResult.id}:${regionId}`) ?? -1;
        if (existingIndex === -1) {
          this._processDownloadedInspectionCollectionElementResult(asset.StructureCampaigns, asset.RepairCampaigns[0], collection, regionId, _elementResult, disposePoint, referenceDate, changeInspectionVisibility, processedSections, pendingByBatch);
        } else {
          this.updatePoint(collection.Elements[existingIndex] as GaugingElement, _elementResult, asset);
        }
        // Yield to main thread every CHUNK_SIZE elements so the render loop stays responsive
        if (i > 0 && i % AssetManagerDownloadedInspection.CHUNK_SIZE === 0) {
          await this._yieldToMainThread();
        }
      }

      this.registerPendingBatches(pendingByBatch);

      delete collection.CollectionInfoModel.ElementsResult;

      // After the main element loop, attach all InstancedMesh objects created for
      // each processed section to section.group so THREE.js group.visible
      // cascade covers them in both SECTIONS and REGIONS view modes (VIS-01).
      // Note: section.surfaceGroup is hidden in REGIONS mode by setViewOnRegionMode();
      // section.group remains visible in all modes so points are never lost.
      for (const section of processedSections) {
        const sectionMeshes = this.instanceManager.getMeshesForSection(section);
        if (sectionMeshes) {
          for (const mesh of sectionMeshes.values()) {
            // Only add if not already in the group (avoids duplicate adds on forceLoad)
            if (!mesh.parent) {
              section.group.add(mesh);
            }
          }
        }

        // DATA-03: drain any SignalR data that arrived before this section loaded
        const pendingData = this.instanceManager.drainPendingData(section.Guid);
        if (pendingData && pendingData.length > 0) {
          for (const pendingResult of pendingData) {
            this._processDownloadedInspectionCollectionElementResult(
              asset.StructureCampaigns, asset.RepairCampaigns[0], collection, regionId,
              pendingResult, disposePoint, referenceDate, changeInspectionVisibility,
              processedSections,
            );
          }
          // Re-check for any new meshes created while draining pending data
          const updatedMeshes = this.instanceManager.getMeshesForSection(section);
          if (updatedMeshes) {
            for (const mesh of updatedMeshes.values()) {
              if (!mesh.parent) {
                section.group.add(mesh);
              }
            }
          }
        }
      }

      // Collect all newly loaded elements and updated elements for this region
      const loadedElements = collection.Elements.filter(element =>
        (element as GaugingElement).regionId === regionId
      ) as GaugingElement[];

      // Emit load event for anomaly labels update if there are elements for this region
      if (loadedElements.length > 0) {
        this.inspectionEventsService.emitLoadEvent(loadedElements);
      }
    }
  }

  /**
   * Pass 2 of loadInspectionElements: register the collected POINT groups in the
   * InstancedMesh manager.
   *
   * A batch for (section, shapeType) may already exist — a second plan or filter
   * in the same region, a point the user inserted (addInstance seeds a batch), or
   * a SignalR point. buildBatch throws on an existing key, which used to abort the
   * whole load and leave the new elements unregistered (invisible, unclickable).
   * Existing batches are therefore extended one instance at a time.
   */
  private registerPendingBatches(pendingByBatch: Map<string, PendingBatch>): void {
    for (const { section, shapeType, items } of pendingByBatch.values()) {
      if (items.length === 0) continue;

      const existingMesh = this.instanceManager.getMeshesForSection(section)?.get(shapeType);
      if (!existingMesh) {
        const mesh = this.instanceManager.buildBatch(section, shapeType, items.map(i => i.element));
        this.instanceManager.registerBulkElements(mesh, items);
        continue;
      }

      for (const { element, matrix, color } of items) {
        this.instanceManager.addInstance(section, shapeType, element, matrix, color);
        // Same reconcile as registerBulkElements (VIS-07): checkElementVisibility may
        // have hidden the phantom before the instance existed, and that call no-oped.
        const phantomVisible = element.SurfaceDesign?.visible ?? true;
        if (!phantomVisible) {
          this.instanceManager.setInstanceVisible(element, false);
        }
      }
    }
  }

  private _processDownloadedInspectionCollectionElementResult(structureCollections: Array<Collection>, repairCampaign: RepairCampaign,
    collection: InspectionCampaign, regionId, _elementResult: ElementResultInfo, disposePoint, referenceDate: Date, changeInspectionVisibility: boolean,
    processedSections?: Set<Section>,
    pendingByBatch?: Map<string, {
      section: Section;
      shapeType: PointShapeType;
      items: { element: GaugingElement; matrix: THREE.Matrix4; color: THREE.Color }[];
    }>): void {
    let _target: WorkableElement = null;
    let type = '';
    let isLine = false;
    for (const structureCollection of structureCollections) {
      if (_elementResult.SectionId) {
        if (structureCollection instanceof StructureCollection)
          _target = structureCollection.getElementById(regionId, _elementResult.SectionId, _elementResult.TargetGeometryId.replace(/-/g, ''));
        else
          _target = structureCollection.getElementById(regionId, _elementResult.SectionId, _elementResult.TargetGeometryId);
        if (_target) break;
      }
      else {
        _target = structureCollection.getElementByIdOnly(regionId, _elementResult.TargetGeometryId.replace(/-/g, ''));
        if (_target) break;
      }
    }

    if (!_target && repairCampaign) {
      var guid = this.converToUUID(_elementResult.TargetGeometryId.replace(/-/g, ''));
      if (guid)
        if (_elementResult.SectionId) {
          _target = repairCampaign.getElementById(regionId, _elementResult.SectionId, guid);
        }
        else {
          _target = repairCampaign.getElementByIdOnly(regionId, guid);
        }
    }

    if (!_target) {
      // Try to create orphan inspection from ElementInfo vertices
      if (_elementResult.ElementInfo?.vertices?.length >= 3) {
        let mesh: THREE.Mesh;
        let material: THREE.Material;

        // Use appropriate material based on inspection method
        if (_elementResult.InspectionMethodId === INSERTIONCONSTANTS.AREA) {
          // Area/Element inspection - use orphan area material with full geometry
          const geometry = new THREE.BufferGeometry();
          geometry.setAttribute('position', new THREE.BufferAttribute(new Float32Array(_elementResult.ElementInfo.vertices), 3));
          if (_elementResult.ElementInfo.faces && _elementResult.ElementInfo.faces.length > 0) geometry.setIndex(_elementResult.ElementInfo.faces);
          geometry.computeVertexNormals();
          material = GraphicsMaterials.orphanInspectionMaterial;
          mesh = new THREE.Mesh(geometry, material);
        } else if(_elementResult.InspectionMethodId === INSERTIONCONSTANTS.POINT){
          material = GraphicsMaterials.pointMaterialMagenta;
          // Point inspection - same shape as a targeted point of the stored scope, orphan (pink) material
          mesh = this.buildOrphanPointMesh(structureCollections, regionId, _elementResult);
          if (mesh) {
            mesh.material = material;
          } else {
            // Unknown scope: simple triangular arrow shape (similar to plate point shape)
            const position = new THREE.Vector3(
              _elementResult.ElementInfo.vertices[0],
              _elementResult.ElementInfo.vertices[1],
              _elementResult.ElementInfo.vertices[2]
            );

            const size = 0.03;
            const vertices = new Float32Array([
              -size, -size * 2, 0,  // bottom left
              -size, size * 2, 0,   // top left
              size * 2, 0, 0,       // right point
            ]);
            const indices = [0, 1, 2];

            const pointGeometry = new THREE.BufferGeometry();
            pointGeometry.setAttribute('position', new THREE.BufferAttribute(vertices, 3));
            pointGeometry.setIndex(indices);
            pointGeometry.computeVertexNormals();

            mesh = new THREE.Mesh(pointGeometry, material);
            mesh.position.copy(position);

            // Try to orient the point using normal if available
            if (_elementResult.ElementInfo.normal?.length >= 3) {
              const normal = new THREE.Vector3(
                _elementResult.ElementInfo.normal[0],
                _elementResult.ElementInfo.normal[1],
                _elementResult.ElementInfo.normal[2]
              );
              mesh.lookAt(position.clone().add(normal));
            }
          }
        }
        else{
          return;
        }

        const inspection = new GaugingElement(mesh, material, [], _elementResult.CheckList, _elementResult);
        inspection.saved = true;
        // isOrphan is set automatically by constructor when targetElements is empty
        // Set region info manually since there's no target
        inspection.regionId = regionId;
        inspection.regionIds = [regionId];
        inspection.SectionId = _elementResult.SectionId;

        collection.addSurfaceElement(inspection);
        inspection.Campaign.CollectionInfoModel.TempCollectionId = collection.CollectionInfoModel.TempCollectionId;
        this.collectionService.addElement(inspection, collection.Date, false);
        inspection.SurfaceDesign.visible = changeInspectionVisibility;

        console.warn("Created orphan inspection (no target):", _elementResult.id, "method:", _elementResult.InspectionMethodId, _elementResult);
      } else {
        console.warn("Cannot create inspection - no target and no vertices:", _elementResult.id, _elementResult);
      }
      return;
    }
    switch (_elementResult.InspectionMethodId) {
      case INSERTIONCONSTANTS.ELEMENT: ///Element
      if (_target instanceof BasicElement) {
        var mesh = this.graphicsRenderDecal.createIconFromPosition(
          [_target.SurfaceDesign.position.x, _target.SurfaceDesign.position.y, _target.SurfaceDesign.position.z],
          _target,
          _target.target.section.sectionNormal.normalize(),
          _target.section.box3
        )
          const inspection = new GaugingElement(mesh, GraphicsMaterials.inspectionMaterial, [_target], _elementResult.CheckList, _elementResult);
          inspection.saved = true;

          collection.addSurfaceElement(inspection);
          inspection.Campaign.CollectionInfoModel.TempCollectionId = collection.CollectionInfoModel.TempCollectionId;

          this.collectionService.addElement(inspection, collection.Date, false);
          inspection.SurfaceDesign.visible = changeInspectionVisibility?_target.section.visible:false;


          const gaugingElement = inspection as GaugingElement
          for (const checkList of gaugingElement.checkList) {
            let formattedDate = new Date(new Date(checkList.Date).toDateString())
            // if (this.currentRegion.Guid === gaugingElement.regionId)
            {
              if (
                formattedDate > this.referenceDate
              ) {
                gaugingElement.date = formattedDate;
                gaugingElement.SurfaceDesign.visible = false
                break
              }
              else {
                gaugingElement.date = formattedDate;

              }
            }
          }
        }
        else if (_target instanceof StructuralElement) {
          const mesh = new THREE.Mesh(_target.SurfaceDesign.geometry.clone(), GraphicsMaterials.inspectionMaterial);
          const inspection = new GaugingElement(mesh, GraphicsMaterials.inspectionMaterial, [_target], _elementResult.CheckList, _elementResult);
          inspection.saved = true;

          collection.addSurfaceElement(inspection);
          inspection.Campaign.CollectionInfoModel.TempCollectionId = collection.CollectionInfoModel.TempCollectionId;

          this.collectionService.addElement(inspection, collection.Date, false);
          inspection.SurfaceDesign.visible = changeInspectionVisibility?_target.section.visible:false;


          const gaugingElement = inspection as GaugingElement
          for (const checkList of gaugingElement.checkList) {
            let formattedDate = new Date(new Date(checkList.Date).toDateString())
            // if (this.currentRegion.Guid === gaugingElement.regionId)
            {
              if (
                formattedDate > this.referenceDate
              ) {
                gaugingElement.date = formattedDate;
                gaugingElement.SurfaceDesign.visible = false
                break
              }
              else {
                gaugingElement.date = formattedDate;

              }
            }
          }
        }
        break;

      case INSERTIONCONSTANTS.AREA:///Area
        {
          const geometry = new THREE.BufferGeometry();
          if (!_elementResult.ElementInfo) return;
          geometry.setAttribute('position', new THREE.BufferAttribute(new Float32Array(_elementResult.ElementInfo.vertices), 3));
          if (_elementResult.ElementInfo.faces && _elementResult.ElementInfo.faces.length > 0) geometry.setIndex(_elementResult.ElementInfo.faces);
          geometry.computeVertexNormals();

          const mesh = new THREE.Mesh(geometry, GraphicsMaterials.inspectionMaterial);
          const inspection = new GaugingElement(mesh, GraphicsMaterials.inspectionMaterial, [_target], _elementResult.CheckList, _elementResult);
          inspection.saved = true;

          collection.addSurfaceElement(inspection);
          inspection.Campaign.CollectionInfoModel.TempCollectionId = collection.CollectionInfoModel.TempCollectionId;

          this.collectionService.addElement(inspection, collection.Date, false);
          inspection.SurfaceDesign.visible = changeInspectionVisibility?_target.section.visible:false;

          const gaugingElement = inspection as GaugingElement
          for (const checkList of gaugingElement.checkList) {
            let formattedDate = new Date(new Date(checkList.Date).toDateString())
            {
              if (
                formattedDate > this.referenceDate
              ) {
                gaugingElement.SurfaceDesign.visible = false
                gaugingElement.date = formattedDate;
                break
              }
              else {
                gaugingElement.date = formattedDate;

              }
            }
          }
        }
        break;
      default:
        switch (_elementResult.Scope) {
          case 'ONLY PLATE':
            type = "Plate";
            break;
          case 'ONLY WEB':
            type = isStiffenerTypeId(_elementResult.TargetGeometryTypeId) ? 'Stiffener' : 'Bracket';
            break;
          case 'ONLY FLANGE':
            type = isStiffenerTypeId(_elementResult.TargetGeometryTypeId) ? 'Stiffener' : 'Bracket';
            break;
          case 'ONLY FACE PLATE':
            type = "FacePlate";
            break;
          case 'ONLY BEAM PART':
            type = "BeamPart";
            break;
          case 'ONLY CABLE':
            type = "Cable";
            break;
          case 'ONLY PIPE':
            type = "Pipe";
            break;
          default:
            if (_target.TypeId === ELEMENTSTYPESIDS.PLATE) {
              type = "Plate";
            }
            else if (_target.TypeId === ELEMENTSTYPESIDS.BRACKET || _target.TypeId === ELEMENTSTYPESIDS.STIFFENER) {
              if (_target.properties.Geometry.WebThickness > 0) {
                type = isStiffenerTypeId(_elementResult.TargetGeometryTypeId) ? 'Stiffener' : 'Bracket';
                _elementResult.Scope = 'ONLY WEB';
              }
              else if (_target.properties.Geometry.FlangeThickness > 0) {
                type = isStiffenerTypeId(_elementResult.TargetGeometryTypeId) ? 'Stiffener' : 'Bracket';
                _elementResult.Scope = 'ONLY FLANGE';
              }
            }
            break;
        }
        if (!_elementResult.ElementInfo) return;

        // let pointNormal = _elementResult.ElementInfo.normal?.length > 0 ? _elementResult.ElementInfo.normal : [_target.section.sectionNormal.x,
        // _target.section.sectionNormal.y, _target.section.sectionNormal.z];
        let pointNormal = _elementResult.ElementInfo.normal?.length > 0 ? new THREE.Vector3(_elementResult.ElementInfo.normal[0],
          _elementResult.ElementInfo.normal[1], _elementResult.ElementInfo.normal[2]) : _target.elementNormal;

        if (type == "Pipe") {
          if (_target.Line?.length >= 6) {
            const i = _target.Line.length
            const start = new THREE.Vector3(_target.Line[0], _target.Line[1], _target.Line[2])
            const end = new THREE.Vector3(_target.Line[i - 3], _target.Line[i - 2], _target.Line[i - 1])
            const line = new THREE.Line3(start, end)

            const point = new THREE.Vector3(_elementResult.ElementInfo.vertices[0], _elementResult.ElementInfo.vertices[1], _elementResult.ElementInfo.vertices[2]);
            let ringPosition;
            ringPosition = line.closestPointToPoint(point, false, ringPosition)

            pointNormal = point.clone().sub(ringPosition).normalize()
          }
        }

        let _inspectionPoint = this.graphicsRenderDecal.createPlatePointFromPosition(_elementResult.ElementInfo.vertices,
          _target, type, _elementResult.Scope, pointNormal, _target.section.box3);

        let inspectionElement: GaugingElement = null;
        if (_elementResult.InspectionTypeId === INSPECTIONS_TYPES_IDS.THICKNESS_GAUGING)
          inspectionElement = new SteelThicknessGaugingPoint(_inspectionPoint.point,
            GraphicsMaterials.pointMaterial, _target, _elementResult.CheckList, _elementResult);
        else
          inspectionElement = new GaugingPoint(_inspectionPoint.point,
            GraphicsMaterials.pointMaterial, _target, _elementResult.CheckList, _elementResult);
        inspectionElement.saved = true;

        // --- InstancedMesh integration (REND-01, REND-03, VIS-01, VIS-03) ---

        // Extract world transform from the temporary pointMesh.
        // createPlatePointFromPosition returns a properly positioned/oriented mesh
        // (position set, lookAt applied for normal-based orientation, scale set).
        // We copy these values into the dummy Object3D and call updateMatrix() to
        // compose the final Matrix4 for setMatrixAt (standard THREE.js dummy pattern).
        this._dummy.position.copy(_inspectionPoint.point.position);
        this._dummy.quaternion.copy(_inspectionPoint.point.quaternion);
        this._dummy.scale.copy(_inspectionPoint.point.scale);
        this._dummy.updateMatrix();

        // Map _elementResult.Scope to PointShapeType for batch selection.
        // After the inner switch above runs, _elementResult.Scope may have been
        // normalized (e.g. 'ONLY WEB' / 'ONLY FLANGE' set explicitly for bracket/stiffener).
        const shapeType = this._scopeToShapeType(_elementResult.Scope);

        // Use default empty-state color — updateAllInspectionColor will apply
        // the correct corrosion color once all elements are loaded (MGR-03).
        const initialColor = new THREE.Color(0xe5e5e5);

        // Accumulate for bulk batch creation (two-pass strategy, REND-07, MGR-02, MGR-05).
        // When pendingByBatch is provided (bulk load path), collect elements by
        // (section, shapeType) group so buildBatch can create exact-capacity meshes.
        // When not provided (SignalR/drain path), fall back to addInstance growth path.
        if (pendingByBatch) {
          const batchKey = `${_target.section.Guid}:${shapeType}`;
          if (!pendingByBatch.has(batchKey)) {
            pendingByBatch.set(batchKey, { section: _target.section, shapeType, items: [] });
          }
          pendingByBatch.get(batchKey)!.items.push({
            element: inspectionElement,
            matrix: this._dummy.matrix.clone(),
            color: initialColor,
          });
        } else {
          // SignalR / drain path: use addInstance growth batch (DynamicDrawUsage, no frustum culling)
          this.instanceManager.addInstance(
            _target.section, shapeType, inspectionElement, this._dummy.matrix.clone(), initialColor,
          );
        }

        // Track the section so we can add InstancedMesh to surfaceGroup after the loop.
        if (processedSections) {
          processedSections.add(_target.section);
        }

        // Keep a reference to the structure section on the element (batch keys,
        // phantom userData and selection fallbacks read element.section), but do
        // NOT register the point in the structure section's surfaceElements —
        // inspection elements must never appear in StructureCampaigns[0].Elements
        // (prod only registers them in the InspectionCampaign; structure-campaign
        // consumers like Load Cycle assume every element there has properties).
        inspectionElement.section = _target.section;

        // Now replace the real mesh with the lightweight phantom proxy.
        // The phantom replaces the real mesh on inspectionElement.SurfaceDesign so
        // 100+ existing .visible/.material access sites work without modification
        // (VIS-03, VIS-05, REND-04).
        // PhantomSurfaceDesign position is stored from the real pointMesh position
        // so callers that read SurfaceDesign.position get a valid value.
        const phantom = new PhantomSurfaceDesign(inspectionElement, this.instanceManager, _inspectionPoint.point.position, _inspectionPoint.point.quaternion, _inspectionPoint.point.scale);
        phantom.userData = { SectionID: _target.section.Guid, scale: _inspectionPoint.point.scale.x };
        inspectionElement.SurfaceDesign = phantom as unknown as THREE.Mesh;

        // Register the element in the InspectionCampaign's internal region/section
        // hierarchy so it appears in collection.Elements. Without this registration,
        // updateAllInspectionColor (which iterates collection.Elements) would skip
        // all POINT elements, leaving them at the initial grey color (0xe5e5e5).
        // Section.addSurfaceElement guards surfaceGroup.add and expandByObject with
        // `instanceof THREE.Object3D` — PhantomSurfaceDesign is not an Object3D so
        // those calls are safely skipped. Restore element.section afterward
        // because collection.addSurfaceElement overwrites it with the collection's
        // internal Section — the structure section reference must be preserved.
        const savedSection = inspectionElement.section;
        collection.addSurfaceElement(inspectionElement);
        inspectionElement.section = savedSection;

        // --- end InstancedMesh integration ---

        if (_target instanceof StructuralElement) (_target as StructuralElement).addGaugingElement(inspectionElement);
        inspectionElement.Campaign.CollectionInfoModel.TempCollectionId = collection.CollectionInfoModel.TempCollectionId;
        this.collectionService.addElement(inspectionElement, collection.Date, false);

        if (repairCampaign) {
          for (const repair of repairCampaign.Elements) {
            if ((repair as StructuralElement).inspectionIds) {
              const index = (repair as StructuralElement).inspectionIds.findIndex(x => x === inspectionElement.Guid);
              if (index > -1) {
                inspectionElement.active = !(repair as StructuralElement).isExecuted;
                (repair as StructuralElement).inspectionsSolved.push(inspectionElement);
              }
            }
          }
        }

        const gaugingElement = inspectionElement as GaugingElement
        this.checkElementVisibility(gaugingElement);
    }
  }

  /**
   * Point mesh of an orphan POINT inspection (target not found) with the shape of its stored scope,
   * built by the same dispatcher as targeted points. `null` when the scope is unknown (caller keeps
   * the fallback triangle). The builders ignore their target argument except Pipe, which reads
   * `properties.Geometry.Diameter`; the stub gives it ORPHAN_PIPE_DIAMETER.
   */
  private buildOrphanPointMesh(structureCollections: Array<Collection>, regionId: string, elementResult: ElementResultInfo): THREE.Mesh | null {
    const { type, scope } = orphanPointShapeFor(elementResult);
    if (!type) return null;
    const vertices = elementResult.ElementInfo.vertices;
    const box = this.findSectionBox(structureCollections, regionId, elementResult.SectionId)
      ?? new THREE.Box3().setFromCenterAndSize(
        new THREE.Vector3(vertices[0], vertices[1], vertices[2]),
        new THREE.Vector3(ORPHAN_DEFAULT_BOX_SIZE, ORPHAN_DEFAULT_BOX_SIZE, ORPHAN_DEFAULT_BOX_SIZE));
    const targetStub = { properties: { Geometry: { Diameter: ORPHAN_PIPE_DIAMETER } } };
    try {
      const built = this.graphicsRenderDecal.createPlatePointFromPosition(vertices, targetStub, type, scope, orphanNormal(elementResult), box);
      return (built?.point as THREE.Mesh) ?? null;
    } catch (e) {
      console.warn('Orphan point shape failed, using fallback triangle:', elementResult.id, e);
      return null;
    }
  }

  /** Non-empty box3 of the inspection's section when that section is loaded in `regionId`, else null. */
  private findSectionBox(structureCollections: Array<Collection>, regionId: string, sectionId: string): THREE.Box3 | null {
    if (!sectionId) return null;
    for (const collection of structureCollections ?? []) {
      const region = collection?.regionsWorkableElements?.find(r => r.Guid === regionId);
      const section = region?.sections?.find(s => s.Guid === sectionId);
      if (section?.box3 && !section.box3.isEmpty()) return section.box3;
    }
    return null;
  }

  /**
   * Map the inspection element Scope string to a PointShapeType enum value.
   *
   * Scope strings are normalized before this call: the inner switch in the
   * POINT case sets _elementResult.Scope to 'ONLY WEB' or 'ONLY FLANGE' for
   * bracket/stiffener elements, so the mapping here is 1:1.
   *
   * Fallback: any unknown scope value maps to PointShapeType.Plate.
   */
  private _scopeToShapeType(scope: string): PointShapeType {
    switch (scope) {
      case 'ONLY PLATE':    return PointShapeType.Plate;
      case 'ONLY WEB':      return PointShapeType.Web;
      case 'ONLY FLANGE':   return PointShapeType.Flange;
      case 'ONLY FACE PLATE': return PointShapeType.FacePlate;
      case 'ONLY BEAM PART':  return PointShapeType.BeamPart;
      case 'ONLY CABLE':    return PointShapeType.Cable;
      case 'ONLY PIPE':     return PointShapeType.Pipe;
      default:              return PointShapeType.Plate;
    }
  }

  private updatePoint(gaugingElement: GaugingElement, elementResultInfo: ElementResultInfo, asset: Asset): void {

    if (gaugingElement.elementResult.TargetGeometryId != elementResultInfo.TargetGeometryId) {
      var target = asset.StructureCampaigns[0]?.Elements.find(x => x.Guid === gaugingElement.elementResult.TargetGeometryId);
      if (!target) {
        target = asset.RepairCampaigns[0]?.Elements.find(x => x.Guid === gaugingElement.elementResult.TargetGeometryId);
      }

      if (target) {
        var index = (target as StructuralElement).gaugingElements.findIndex(x => x.Guid === gaugingElement.Guid);
        if (index > -1) {
          (target as StructuralElement).gaugingElements = (target as StructuralElement).gaugingElements.splice(index, 1);
        }
      }

      target = asset.StructureCampaigns[0]?.Elements.find(x => x.Guid === elementResultInfo.TargetGeometryId);
      if (!target) {
        var guid = this.converToUUID(elementResultInfo.TargetGeometryId);

        target = asset.RepairCampaigns[0]?.Elements.find(x => x.Guid === guid);
      }

      if (target) {
        (target as StructuralElement).gaugingElements.push(gaugingElement);
      }
    }

    gaugingElement.elementResult = elementResultInfo;
    this.checkElementVisibility(gaugingElement);
  }

  private checkElementVisibility(gaugingElement: GaugingElement) {
    if (gaugingElement.elementResult.Active) {
      for (const checkList of gaugingElement.checkList) {
        let formattedDate = new Date(new Date(checkList.Date).toDateString())
        if (
          formattedDate > this.referenceDate
        ) {
          gaugingElement.date = formattedDate;
          gaugingElement.SurfaceDesign.visible = false
          break;
        }
        else {
          gaugingElement.date = formattedDate;
          break;

        }
      }
    }
    else {
      if (gaugingElement.elementResult.Active === false) {
        var endDate = new Date(new Date(gaugingElement.elementResult.EndDate.toString().replace('Z', '')).toDateString())
        if (endDate <= this.referenceDate)
          gaugingElement.SurfaceDesign.visible = false
      }
    }
  }


  private insertString(str, index, value) {
    return str.substr(0, index) + value + str.substr(index);
  }

  private converToUUID(str) {
    var _uuid = str;
    _uuid = this.insertString(_uuid, 8, '-')
    _uuid = this.insertString(_uuid, 13, '-')
    _uuid = this.insertString(_uuid, 18, '-')
    _uuid = this.insertString(_uuid, 23, '-')

    return _uuid
  }

}
